import { createSelector } from 'reselect';
import transformToTimeSeries, {
  seriesArraySortDesending,
} from '@/pages/CompanyReport/utils/tranformToTimeSeries';
import {
  getSummary,
  getHealthStatements,
  getAnalysis,
  getFormatting,
  getHealthCheck,
} from '@/pages/CompanyReport/redux/selectors';
import { toCurrencyTruncated } from '@simplywallst/formatting';
import type {
  Health,
  HistoricalDebtEquityRawData,
  BalanceSheetEntity,
} from './interfaces';
import { minBy, maxBy } from 'lodash';
import { stratify } from 'd3-hierarchy';

const formatter = (significants, reportingCurrencyISO) => (value) =>
  value !== null
    ? toCurrencyTruncated(value, significants, reportingCurrencyISO)
    : 'n/a';

export const getHealthSelector = createSelector(
  [getSummary, getHealthStatements, getAnalysis, getHealthCheck, getFormatting],
  (summary, statements, analysis, healthChecks, formatting): Health => {
    const isDataMeaningless =
      statements.isHealthAnalysisMeaningless.value === 'Pass';

    const isBank = statements.isBank.value === 'Pass';

    const reportingCurrencyISO = formatting.reportingCurrencyISO;
    const reportingUnit = formatting.reportingUnit;

    const currentAssets = analysis.currentAssets * reportingUnit;
    const currentLiabilities = analysis.totalCurrentLiab * reportingUnit;
    const totalAssets = analysis.totalAssets * reportingUnit;
    const totalLiabsEquity = analysis.totalLiabEquity * reportingUnit;
    const currentEquity = analysis.totalEquity * reportingUnit;
    const currentAP = analysis.accountsPayable * reportingUnit;
    const currentDebt = analysis.totalDebt * reportingUnit;
    const currentCash = analysis.cashStInvestments * reportingUnit;
    const currentReceiveCash = analysis.receivables * reportingUnit;
    const currentInventory = analysis.inventory * reportingUnit;
    const currentPhysicalAssets = analysis.PPE * reportingUnit;

    const format0DP = formatter(0, reportingCurrencyISO);
    const format2DP = formatter(2, reportingCurrencyISO);

    const shortTermAssets = currentAssets;
    const shortTermLiabilities = currentLiabilities;
    const longTermAssets = totalAssets - currentAssets;
    const longTermLiabilities =
      totalLiabsEquity - currentEquity - currentLiabilities;

    const shortTermAssetsFormatted0DP = format0DP(shortTermAssets);
    const shortTermAssetsFormatted2DP = format2DP(shortTermAssets);
    const shortTermLiabilitiesFormatted0DP = format0DP(shortTermLiabilities);
    const shortTermLiabilitiesFormatted2PD = format2DP(shortTermLiabilities);
    const longTermAssetsFormatted0DP = format0DP(longTermAssets);
    const longTermAssetsFormatted2DP = format2DP(longTermAssets);
    const longTermLiabilitiesFormatted0DP = format0DP(longTermLiabilities);
    const longTermLiabilitiesFormatted2DP = format2DP(longTermLiabilities);

    /**
     * Debt To Equity History
     */
    const debt = analysis.legacyChartData.totalDebtHistory;
    const equity = analysis.legacyChartData.totalEquity;
    const cash = analysis.legacyChartData.pastCashInvestments;

    const debtMax = maxBy(debt, (n) => n[1]);
    const equityMax = maxBy(equity, (n) => n[1]);
    const cashMax = maxBy(cash, (n) => n[1]);

    const debtMin = minBy(debt, (n) => n[1]);
    const equityMin = minBy(equity, (n) => n[1]);
    const cashMin = minBy(cash, (n) => n[1]);

    /**
     * Balance Sheet
     */
    const liabilities = Math.round(
      totalLiabsEquity - currentEquity - currentDebt - currentAP
    );
    const assets = Math.round(
      totalAssets -
        currentCash -
        currentReceiveCash -
        currentInventory -
        currentPhysicalAssets
    );

    const balanceSheetData: BalanceSheetEntity[] = [
      {
        name: 'liabilities',
        parent: null,
        value: null,
        emphasis: 'good',
      },
      {
        name: 'Equity',
        parent: 'liabilities',
        value: currentEquity,
        formattedValue: formatter(1, reportingCurrencyISO)(currentEquity),
        emphasis: 'good',
      },
      {
        name: 'Debt',
        parent: 'liabilities',
        value: currentDebt,
        formattedValue: formatter(1, reportingCurrencyISO)(currentDebt),
        emphasis: 'bad',
      },
      {
        name: 'Accounts Payable',
        parent: 'liabilities',
        value: currentAP,
        formattedValue: formatter(1, reportingCurrencyISO)(currentAP),
        emphasis: 'good',
      },
      {
        name: 'Other Liabilities',
        parent: 'liabilities',
        value: liabilities,
        formattedValue: formatter(1, reportingCurrencyISO)(liabilities),
        emphasis: 'good',
      },
      {
        name: 'assets',
        parent: null,
        value: null,
        emphasis: 'good',
      },
      {
        name: 'Cash & Short term Investments',
        parent: 'assets',
        value: currentCash,
        formattedValue: formatter(1, reportingCurrencyISO)(currentCash),
        emphasis: 'good',
      },
      {
        name: 'Receivables',
        parent: 'assets',
        value: currentReceiveCash,
        formattedValue: formatter(1, reportingCurrencyISO)(currentReceiveCash),
        emphasis: 'good',
      },
      {
        name: 'Inventory',
        parent: 'assets',
        value: currentInventory,
        formattedValue: formatter(1, reportingCurrencyISO)(currentInventory),
        emphasis: 'good',
      },
      {
        name: 'Physical Assets',
        parent: 'assets',
        value: currentPhysicalAssets,
        formattedValue: formatter(
          1,
          reportingCurrencyISO
        )(currentPhysicalAssets),
        emphasis: 'good',
      },
      {
        name: 'Long term & Other Assets',
        parent: 'assets',
        value: assets,
        formattedValue: formatter(1, reportingCurrencyISO)(assets),
        emphasis: 'good',
      },
    ];

    const liabilitiesBalanceSheet = stratify<BalanceSheetEntity>()
      .id((d) => d.name)
      .parentId((d) => d.parent)(
        balanceSheetData.filter(
          (n) => n.name === 'liabilities' || n.parent === 'liabilities'
        )
      )
      .sum((d) => d.value || 0);

    const assetsBalanceSheet = stratify<BalanceSheetEntity>()
      .id((d) => d.name)
      .parentId((d) => d.parent)(
        balanceSheetData.filter(
          (n) => n.name === 'assets' || n.parent === 'assets'
        )
      )
      .sum((d) => d.value || 0);

    return {
      companyId: summary.companyId,
      companyName: summary.shortName,
      healthChecks,
      isDataMeaningless,
      isBank,
      isFund: summary.isFund,
      longTermAssets,
      longTermAssetsFormatted0DP,
      longTermAssetsFormatted2DP,
      longTermLiabilities,
      longTermLiabilitiesFormatted0DP,
      longTermLiabilitiesFormatted2DP,
      reportingCurrencyISO,
      reportingUnit,
      score: summary.score.health,
      shortTermAssets,
      shortTermAssetsFormatted0DP,
      shortTermAssetsFormatted2DP,
      shortTermLiabilities,
      shortTermLiabilitiesFormatted0DP,
      shortTermLiabilitiesFormatted2PD,
      statements,
      uniqueSymbol: summary.uniqueSymbol,
      /**
       * Debt To Equity History
       */
      debt,
      equity,
      cash,
      debtMax: debtMax ? debtMax[1] : 0,
      equityMax: equityMax ? equityMax[1] : 0,
      cashMax: cashMax ? cashMax[1] : 0,
      debtMin: debtMin ? debtMin[1] : 0,
      equityMin: equityMin ? equityMin[1] : 0,
      cashMin: cashMin ? cashMin[1] : 0,
      /**
       * Balance Sheet
       */
      liabilitiesBalanceSheet,
      assetsBalanceSheet,
    };
  }
);

export const financialPositionAnalysisPropFromState = createSelector(
  [getHealthSelector],
  (health) => {
    return {
      companyName: health.companyName,
      shortTermAssets: health.shortTermAssets,
      shortTermLiabilities: health.shortTermLiabilities,
      longTermAssets: health.longTermAssets,
      longTermLiabilities: health.longTermLiabilities,
      shortTermAssetsFormatted0DP: health.shortTermAssetsFormatted0DP,
      shortTermAssetsFormatted2DP: health.shortTermAssetsFormatted2DP,
      shortTermLiabilitiesFormatted0DP: health.shortTermLiabilitiesFormatted0DP,
      shortTermLiabilitiesFormatted2PD: health.shortTermLiabilitiesFormatted2PD,
      longTermAssetsFormatted0DP: health.longTermAssetsFormatted0DP,
      longTermAssetsFormatted2DP: health.longTermAssetsFormatted2DP,
      longTermLiabilitiesFormatted0DP: health.longTermLiabilitiesFormatted0DP,
      longTermLiabilitiesFormatted2DP: health.longTermLiabilitiesFormatted2DP,
    };
  }
);

export const debtEquityHistoryAnalysisPropsFromState = createSelector(
  [getHealthSelector],
  (health) => {
    return {
      cash: health.cash,
      cashMax: health.cashMax,
      cashMin: health.cashMin,
      currencyISO: health.reportingCurrencyISO,
      debt: health.debt,
      debtMax: health.debtMax,
      debtMin: health.debtMin,
      equity: health.equity,
      equityMax: health.equityMax,
      equityMin: health.equityMin,
      reportingUnit: health.reportingUnit,
    };
  }
);

export const getHistoricalDebtEquityRawDataSelector = createSelector(
  [getSummary, getAnalysis, getFormatting],
  (summary, analysis, formatting): HistoricalDebtEquityRawData => {
    const { legacyChartData } = analysis;

    const totalEquity = legacyChartData.totalEquity;
    const totalDebt = legacyChartData.totalDebtHistory;
    const cashAndInvestments = legacyChartData.pastCashInvestments;

    const debtTimeSeries = transformToTimeSeries([
      totalEquity,
      totalDebt,
      cashAndInvestments,
    ]).sort(seriesArraySortDesending);

    return {
      uniqueSymbol: summary.uniqueSymbol,
      reportingCurrencyISO: formatting.reportingCurrencyISO,
      reportingUnit: formatting.reportingUnitText,
      lastEarningsUpdate: analysis.lastEarningsUpdateTimeStamp,
      companyShortName: summary.shortName,
      pastDebtAndEquityData: debtTimeSeries,
    };
  }
);
